import { RightOutlined } from "@ant-design/icons";
import { Card, Tag, Typography } from "antd";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import { formatDisplayDateOnly, getRouteWithParams, ROUTES } from "~/utils";
import type { HomePendingLease } from "../utils/buildHomePendingLeases";

const { Text, Title } = Typography;

const MAX_VISIBLE = 3;

const KIND_TAG_COLOR: Record<HomePendingLease["kind"], string> = {
  submitted: "blue",
  under_review: "gold",
  approved_pre_move_in: "orange",
};

export type HomeNextStepsProps = {
  pendingLeases: HomePendingLease[];
};

export function HomeNextSteps({ pendingLeases }: HomeNextStepsProps) {
  const { t, i18n } = useAppTranslation();

  const visible = pendingLeases.slice(0, MAX_VISIBLE);
  const hasMore = pendingLeases.length > MAX_VISIBLE;

  const rows = useMemo(
    () =>
      visible.map((lease) => {
        let statusLabel: string;
        switch (lease.kind) {
          case "submitted":
            statusLabel = t("Home_lease_step_submitted");
            break;
          case "under_review":
            statusLabel = t("Home_lease_step_under_review");
            break;
          case "approved_pre_move_in":
            if (lease.moveInDate) {
              statusLabel = t("Home_lease_step_move_in_date", {
                date: formatDisplayDateOnly(lease.moveInDate, i18n.language),
              });
            } else {
              statusLabel = t("Home_lease_step_approved");
            }
            break;
        }
        return { lease, statusLabel };
      }),
    [visible, t, i18n.language]
  );

  if (pendingLeases.length === 0) return null;

  return (
    <section style={{ width: "100%" }}>
      <Title level={4} style={{ marginBottom: 12 }}>
        {t("Home_next_steps_title")}
      </Title>
      <Card styles={{ body: { padding: 0 } }}>
        {rows.map(({ lease, statusLabel }, index) => (
          <Link
            key={lease.id}
            to={getRouteWithParams("leaseDetails", { leaseID: lease.id })}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "16px 20px",
              color: "inherit",
              borderBottom:
                index < rows.length - 1 || hasMore
                  ? "1px solid #f0f0f0"
                  : undefined,
            }}
          >
            <Tag color={KIND_TAG_COLOR[lease.kind]} style={{ margin: 0 }}>
              {statusLabel}
            </Tag>
            <Text strong style={{ flex: 1, minWidth: 0 }}>
              {lease.label}
            </Text>
            <Text type="secondary" style={{ flexShrink: 0 }}>
              {t("View")}
            </Text>
            <RightOutlined style={{ color: "rgba(0, 0, 0, 0.25)", fontSize: 12 }} />
          </Link>
        ))}
        {hasMore ? (
          <Link
            to={ROUTES.lease}
            style={{
              display: "block",
              padding: "12px 20px",
              textAlign: "center",
            }}
          >
            {t("Home_next_steps_view_all_leases")}
          </Link>
        ) : null}
      </Card>
    </section>
  );
}
